/**
 * Product Preview Modal
 * Displays product information without purchase elements (price, buy buttons, quantity, variants, rating)
 * Inspired by quick-add-modal but with filtered content for informational purposes only
 */
if (!customElements.get('product-preview-modal')) {
  customElements.define(
    'product-preview-modal',
    class ProductPreviewModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="ProductPreviewInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        this.modalContent.innerHTML = '';
        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');

        const spinner = opener.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            if (productElement) {
              this.preprocessHTML(productElement);
              HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

              // Initialize media gallery and product modal scripts
              this.initializeMediaScripts();

              if (window.Shopify && Shopify.PaymentButton) {
                Shopify.PaymentButton.init();
              }
              if (window.ProductModel) window.ProductModel.loadShopifyXR();
            }

            super.show(opener);
          })
          .catch((error) => {
            console.error('Error loading product preview:', error);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            const spinner = opener.querySelector('.loading__spinner');
            if (spinner) spinner.classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        // Copy color scheme classes to modal content
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient') {
            this.modalContent.classList.add(classApplied);
          }
        });

        this.preventDuplicatedIDs(productElement);
        this.removeSpecificElements(productElement);
        if (this.dataset.knowfitPreviewOwner !== undefined) {
          productElement.querySelectorAll('.nutrition-summary').forEach((element) => element.remove());
          productElement.querySelectorAll('.nutrition-table-content').forEach((element) => {
            (element.closest('.product__accordion') || element).remove();
          });
        }
        // Don't simplify gallery - show all images like main-product
        // this.simplifyGallery(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      /**
       * Remove purchase-related elements only:
       * - Price block
       * - Buy buttons
       * - Quantity selector
       * - Variant picker
       * - Rating
       * - ...
       * IMPORTANT: Be specific with selectors to avoid removing content we want to keep
       */
      removeSpecificElements(productElement) {
        // Specific selectors for elements to remove
        const selectorsToRemove = [
          // Price block (be specific)
          '.price:not(.price-per-item__container)',
          '.no-js-inline',

          // Buy buttons and product form
          'product-form',
          '.product-form__buttons',
          '.shopify-payment-button',

          // Quantity selector
          '[id^="Quantity-Form-"]',
          '.product-form__quantity',

          // Variant picker
          'variant-selects',
          'variant-radios',

          // Rating
          '.rating-wrapper',
          '.rating',

          // Pickup availability
          'pickup-availability',

          // Product modal (image zoom) and nested modals
          'product-modal',
          'modal-dialog',

          // Volume pricing notes
          '.volume-pricing-note',

          // Flow buttons block (subscription / one-time choice)
          '.product__flow-buttons',

          // Gallery elements - remove slider buttons but keep thumbnails
          '.slider-buttons',
          '.slider-counter',
          // Keep thumbnails - they replace slider buttons
          // '.thumbnail-slider',
          // '.thumbnail-list',
          // '[id^="GalleryThumbnails-"]',
          '.product__media-icon',

          // Media zoom toggle buttons (product-modal is already removed above)
          '.product__media-toggle',
          '.product__media-zoom'
        ];

        selectorsToRemove.forEach((selector) => {
          try {
            const elements = productElement.querySelectorAll(selector);
            elements.forEach((el) => el.remove());
          } catch (e) {
            // Ignore invalid selectors
          }
        });
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `preview-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);

        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      /**
       * Simplify gallery to show only the main (first) image
       * Removes thumbnails, navigation, and additional media items
       */
      simplifyGallery(productElement) {
        // Remove all media items except the first one
        const mediaItems = productElement.querySelectorAll('.product__media-item');
        mediaItems.forEach((item, index) => {
          if (index > 0) item.remove();
        });

        // Remove slider buttons and counter
        const sliderButtons = productElement.querySelectorAll('.slider-buttons, .slider-counter');
        sliderButtons.forEach((el) => el.remove());

        // Remove only the zoom icon spans (not the container that holds the image)
        const zoomIcons = productElement.querySelectorAll('.product__media-icon');
        zoomIcons.forEach((el) => el.remove());

        // Remove gallery list semantic for accessibility
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (galleryList) {
          galleryList.setAttribute('role', 'presentation');
        }
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      initializeMediaScripts() {
        // Ensure media-gallery.js and product-modal.js are loaded
        // They should already be loaded from main-product, but ensure they're initialized
        if (window.MediaGallery) {
          // MediaGallery should auto-initialize on custom elements
          const mediaGalleries = this.modalContent.querySelectorAll('media-gallery');
          mediaGalleries.forEach((gallery) => {
            if (!gallery.initialized) {
              // Force initialization if needed
              gallery.connectedCallback?.();
            }
          });
        }

        // Ensure slider components are initialized
        const sliders = this.modalContent.querySelectorAll('slider-component');
        sliders.forEach((slider) => {
          if (slider && typeof slider.init === 'function') {
            slider.init();
          }
        });
      }
    }
  );
}
